import { Injectable, InjectionToken, inject } from '@angular/core';
import { Router } from '@angular/router';

/** Seam for `window` so the reload path is unit-testable without navigating the test runner. */
export const CHUNK_RECOVERY_WINDOW = new InjectionToken<Window>('Chunk Recovery Window', {
  providedIn: 'root',
  factory: () => window,
});

export const CHUNK_RELOAD_GUARD_KEY = 'rkt.chunkReloadAt';

/** A reload inside this window means the reload itself did not fix the problem, so stop. */
export const CHUNK_RELOAD_WINDOW_MS = 10_000;

/** What recovery did, so the caller can report it. 'gave-up' is a real user-facing failure. */
export type ChunkRecoveryStage = 'retry' | 'reload' | 'gave-up';

/* The browsers disagree on the wording. The MIME variant appears whenever the host answers a module
 * request with the SPA index instead of the file, which is the live behaviour on Firebase Hosting:
 * its catch-all rewrite cannot be narrowed to exclude assets, so a chunk removed by a deploy comes
 * back as 200 text/html rather than a 404. All wordings are kept because any of them can reach us
 * depending on the browser. */
const CHUNK_FAILURE_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
  'is not a valid javascript mime type',
  'expected a javascript module script',
  'loading chunk',
  'loading css chunk',
];

/**
 * True when an error is a build asset that could not be loaded, rather than an ordinary runtime
 * fault. Deliberately narrow: a false positive here reloads the app out from under a user who hit
 * a plain bug, which is worse than the failure this recovers from.
 */
export const isChunkLoadFailure = (err: unknown): boolean => {
  if (!err) {
    return false;
  }

  if (typeof err === 'object' && (err as Error).name === 'ChunkLoadError') {
    return true;
  }

  const message = typeof err === 'string' ? err : ((err as Error).message ?? '');

  if (!message) {
    return false;
  }

  const normalized = message.toLowerCase();

  return CHUNK_FAILURE_PATTERNS.some((pattern) => normalized.includes(pattern));
};

/**
 * Recovers a user stranded on a superseded bundle: retry the route once (covers a request that
 * merely raced the deploy), then reload onto the current build. The reload is guarded so a chunk
 * that is broken for any other reason cannot put the app in a reload loop.
 */
@Injectable({ providedIn: 'root' })
export class ChunkLoadRecoveryService {
  private readonly win = inject(CHUNK_RECOVERY_WINDOW);
  private readonly router = inject(Router);

  /** Routes already retried in this page load. Reset by the reload, which is the point. */
  private readonly retried = new Set<string>();

  /** Used only when sessionStorage is unavailable (Safari private mode throws on access). */
  private inMemoryReloadAt: number | null = null;

  /**
   * @param canRetry false when re-running the navigation cannot re-attempt the failed import, so
   * the retry would be a wasted no-op. Only the router's navigation error handler can genuinely
   * retry; a dynamic import that failed outside routing cannot.
   * @returns the stage taken. 'gave-up' means recovery is exhausted and the caller should let the
   * error surface so Sentry records a genuine user-facing failure.
   */
  public recover(url: string, { canRetry = true }: { canRetry?: boolean } = {}): ChunkRecoveryStage {
    if (canRetry && !this.retried.has(url)) {
      this.retried.add(url);
      // If the retry fails too, the router reports it through the same navigation error handler,
      // which lands on the reload branch below. The rejected promise itself carries no extra
      // information, so it is swallowed rather than left as an unhandled rejection.
      this.router.navigateByUrl(url).catch(() => undefined);

      return 'retry';
    }

    return this.reloadOnce();
  }

  private reloadOnce(): ChunkRecoveryStage {
    const now = Date.now();
    const lastReloadAt = this.readReloadAt();

    if (lastReloadAt !== null && now - lastReloadAt < CHUNK_RELOAD_WINDOW_MS) {
      return 'gave-up';
    }

    this.writeReloadAt(now);
    // reload(), not replace(location.href): under hash routing the current URL always carries a
    // fragment, and navigating to a URL that differs only in (or not at all from) its fragment is
    // a same-document navigation - it would scroll and never refetch the document.
    this.win.location.reload();

    return 'reload';
  }

  private readReloadAt(): number | null {
    try {
      const raw = this.win.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
      const parsed = raw === null ? Number.NaN : Number(raw);

      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return this.inMemoryReloadAt;
    }
  }

  private writeReloadAt(at: number): void {
    this.inMemoryReloadAt = at;

    try {
      this.win.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(at));
    } catch {
      /* Storage unavailable - inMemoryReloadAt above still guards this page load. */
    }
  }
}

/**
 * Shape of the router's NavigationError that this handler needs. Declared structurally so the
 * handler can be unit-tested without constructing a real NavigationError.
 */
export interface ChunkNavigationError {
  error: unknown;
  url: string;
}

/**
 * The router's navigation-error handler. Extracted from the provider so it is unit-testable: the
 * provider itself is never instantiated in a spec. Angular runs the handler inside an injection
 * context, so `inject` here is valid.
 *
 * @param recovery resolved by the caller; a parameter rather than an `inject` call so the handler
 * is callable directly from a test.
 */
export const handleChunkNavigationError = (
  event: ChunkNavigationError,
  recovery: ChunkLoadRecoveryService,
): ChunkRecoveryStage | undefined => {
  if (!isChunkLoadFailure(event.error)) {
    return undefined;
  }

  return recovery.recover(event.url);
};
